import React from 'react';
import styled from 'styled-components/macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faImages, faVideo } from '@fortawesome/pro-regular-svg-icons';
import { Card } from 'src/client/components/ui/Card';
import { Link } from 'react-router-dom';
import { Badge } from 'src/client/components/ui/Badge';
import { Badges } from 'src/client/components/ui/Badges';
import { RatingOverlay } from 'src/client/components/ui/RatingOverlay';
import { faThumbsUp } from '@fortawesome/pro-solid-svg-icons';
import cx from 'classnames';
import { formatDateDefaultOptions, secondsToRuntime } from 'src/client/utils/converters';
import { ProgressBar } from 'src/client/components/ProgressBar/ProgressBar';
import { RibbonCorner } from 'src/client/components/Ribbon/RibbonCorner';
import { CardModelLinks } from 'src/client/containers/Cards/CardModelLinks';
import { GalleryModel } from '@tovia/man-protos/dist/types/content.types';
import { BlurrableLazyImage } from 'src/client/components/ui/BlurredImage';
import { buildSearchPath } from 'src/shared/sanitize';

type Props = {
  categories?: string[];
  favoriteCount?: number;
  imageCount?: number;
  imgAlt: string;
  imgSrc: string;
  isBlurred: boolean;
  isIntimateSelection?: boolean;
  isMovie?: boolean;
  isNetworkWideGallery?: boolean;
  isPromoGallery?: boolean;
  isStaffSelection?: boolean;
  models: Pick<GalleryModel, 'name' | 'path'>[];
  movieProgress?: number;
  // @TODO fix these any's
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  photographer: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ratingStars: any;
  runtime?: number;
  showCategories?: boolean;
  showModelName?: boolean;
  showPhotographer?: boolean;
  timestamp?: string;
  title: string;
  url: string;
  useExternalLinkForModelOrArtist?: boolean;
  views?: string | number;
};

export const GalleryDetailedCard = (props: Props) => {
  const {
    categories = [],
    favoriteCount = 0,
    imageCount = 0,
    imgAlt,
    imgSrc,
    isBlurred,
    isMovie,
    isNetworkWideGallery,
    isPromoGallery,
    models,
    movieProgress = 0,
    photographer,
    ratingStars,
    runtime = 0,
    showCategories,
    showPhotographer = true,
    timestamp = '',
    title,
    url,
    useExternalLinkForModelOrArtist,
    views = 0,
  } = props;

  const ribbonContent = isMovie ? 'BONUS FILM' : 'BONUS SET';

  const usersRating = ratingStars.rating !== 0 ? ratingStars.rating : 'Not rated yet';

  const attrs = [
    {
      label: 'Released',
      value: new Intl.DateTimeFormat('en-US', { ...formatDateDefaultOptions, month: 'short' }).format(
        new Date(timestamp),
      ),
    },
    {
      label: 'Featuring',
      value: <CardModelLinks models={models} useExternalLinkForModelOrArtist={useExternalLinkForModelOrArtist} />,
    },
    ...(showPhotographer && !props.isIntimateSelection
      ? [
          {
            label: runtime ? 'Director' : 'Photographer',
            value: useExternalLinkForModelOrArtist ? (
              <a href={photographer.url} key={photographer.url}>
                {photographer.text}
              </a>
            ) : (
              <Link to={photographer.url} key={photographer.url}>
                {photographer.text}
              </Link>
            ),
          },
        ]
      : []),
    ...(showCategories && categories.length > 0
      ? [
          {
            label: 'Categories',
            value: categories
              .map((cat) => (
                <Link to={buildSearchPath([cat])} key={`${title}_${cat}`}>
                  {cat}
                </Link>
              ))
              .reduce((result, value) => (
                <>
                  {result}, {value}
                </>
              )),
          },
        ]
      : []),
    {
      label: ratingStars.userRating ? 'Your Rating' : 'Rating',
      value: (
        <RatingOverlay ratingStars={ratingStars}>
          <span className="rating-overlay">{ratingStars.userRating ? ratingStars.userRating : usersRating}</span>
        </RatingOverlay>
      ),
    },
  ];

  return (
    <Wrapper className="detailed-card-wrapper">
      <Card className={cx({ highlighted: isNetworkWideGallery })}>
        <div className="photo" style={{ position: 'relative' }}>
          <RibbonCorner text={isPromoGallery ? ribbonContent : ''} ribbonType="detail" />
          <Link to={url}>
            <BlurrableLazyImage
              src={imgSrc}
              alt={imgAlt}
              width="100%"
              height="100%"
              lazy
              animate
              $isBlurred={isBlurred}
            />
          </Link>
          <ProgressBar progress={movieProgress} />
        </div>
        <div className="photo-stats">
          <h3 title={title}>
            <Link to={url}>{title}</Link>
          </h3>
          <div className="attrs">
            {attrs.map((attr) => (
              <div className="attr" key={attr.label}>
                <span className="attr-label">{attr.label}: </span>
                <span className="attr-value">{attr.value}</span>
              </div>
            ))}
          </div>
          <Badges>
            {imageCount ? <Badge key="photos" text={imageCount} icon={<FontAwesomeIcon icon={faImages} />} /> : null}
            {runtime > 0 ? (
              <Badge key="runtime" text={secondsToRuntime(runtime)} icon={<FontAwesomeIcon icon={faVideo} />} />
            ) : null}
            <Badge key="views" text={views} icon={<FontAwesomeIcon icon={faEye} />} />
            <Badge key="favorites" text={favoriteCount} icon={<FontAwesomeIcon icon={faThumbsUp} />} />
          </Badges>
        </div>
      </Card>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  &:not(.menu-opened) {
    &:hover {
      .card-menu-overlay {
        opacity: 1;
      }
    }
  }

  .card-menu-overlay {
    transition: 0.3s opacity;
    opacity: 0;
    color: #fff;
  }

  .card-info-1 {
    padding-bottom: 0;
  }
  .card-info-2 {
    padding-top: 0;
  }

  .card-media {
    overflow: hidden;
  }
`;
